import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Lagerbewegung } from '../types';
import './LagerbewegungList.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

const LagerbewegungList: React.FC = () => {
  const [bewegungen, setBewegungen] = useState<Lagerbewegung[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterTyp, setFilterTyp] = useState<string>('');

  const loadBewegungen = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterTyp) params.append('typ', filterTyp);
      const url = `${API_URL}/api/lagerbewegungen${params.toString() ? '?' + params.toString() : ''}`;
      const response = await axios.get(url);
      setBewegungen(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Lagerbewegungen:', error);
    } finally {
      setLoading(false);
    }
  }, [filterTyp]);

  useEffect(() => {
    loadBewegungen();
  }, [loadBewegungen]);

  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTypClass = (typ: string) => {
    const map: Record<string, string> = {
      'Eingang': 'bewegung-typ-eingang',
      'Ausgang': 'bewegung-typ-ausgang',
      'Umlagerung': 'bewegung-typ-umlagerung',
      'Inventur': 'bewegung-typ-inventur',
      'Verschnitt': 'bewegung-typ-verschnitt',
    };
    return map[typ] || '';
  };

  const getTypIcon = (typ: string) => {
    const map: Record<string, string> = {
      'Eingang': '\u2B07',
      'Ausgang': '\u2B06',
      'Umlagerung': '\u21C4',
      'Inventur': '\uD83D\uDCCB',
      'Verschnitt': '\u2702',
    };
    return map[typ] || '';
  };

  const getLagerplatzDisplay = (bewegung: Lagerbewegung) => {
    if (bewegung.typ === 'Umlagerung') {
      return `${bewegung.lagerplatz_von || '?'} \u2192 ${bewegung.lagerplatz_nach || '?'}`;
    }
    if (bewegung.typ === 'Eingang') {
      return bewegung.lagerplatz_nach || '-';
    }
    if (bewegung.typ === 'Ausgang') {
      return bewegung.lagerplatz_von || '-';
    }
    return bewegung.lagerplatz_von || bewegung.lagerplatz_nach || '-';
  };

  return (
    <div className="bewegung-list-container">
      <div className="list-header">
        <h2>Lagerbewegungen</h2>
        <div className="header-actions">
          <select
            value={filterTyp}
            onChange={(e) => setFilterTyp(e.target.value)}
            className="filter-select"
          >
            <option value="">Alle Typen</option>
            <option value="Eingang">Eingang</option>
            <option value="Ausgang">Ausgang</option>
            <option value="Umlagerung">Umlagerung</option>
            <option value="Inventur">Inventur</option>
            <option value="Verschnitt">Verschnitt</option>
          </select>
          <button className="btn-refresh" onClick={loadBewegungen}>Aktualisieren</button>
        </div>
      </div>

      {loading ? (
        <div className="loading">L&auml;dt...</div>
      ) : bewegungen.length === 0 ? (
        <div className="empty-state">
          <p>Keine Lagerbewegungen vorhanden</p>
          <p className="empty-hint">Lagerbewegungen werden automatisch erstellt, wenn Waren ein-/ausgebucht werden (z.B. via Chat).</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="bewegung-table">
            <thead>
              <tr>
                <th>Typ</th>
                <th>Zeitpunkt</th>
                <th>Artikel</th>
                <th>Menge</th>
                <th>Lagerplatz</th>
                <th>Charge</th>
                <th>Referenz</th>
                <th>Benutzer</th>
              </tr>
            </thead>
            <tbody>
              {bewegungen.map((b) => (
                <tr key={b.bewegung_id} className="bewegung-row">
                  <td>
                    <span className={`typ-badge ${getTypClass(b.typ)}`}>
                      {getTypIcon(b.typ)} {b.typ}
                    </span>
                  </td>
                  <td className="datum-cell">{formatDateTime(b.zeitstempel)}</td>
                  <td>{b.artikel_bezeichnung || `#${b.artikel_id}`}</td>
                  <td className="menge-cell">
                    <span className={b.typ === 'Ausgang' ? 'menge-negativ' : 'menge-positiv'}>
                      {b.typ === 'Ausgang' ? '-' : '+'}{b.menge} {b.artikel_einheit || ''}
                    </span>
                  </td>
                  <td className="lagerplatz-cell">{getLagerplatzDisplay(b)}</td>
                  <td>{b.charge_nr || '-'}</td>
                  <td>
                    {b.referenz_typ
                      ? `${b.referenz_typ} #${b.referenz_id}`
                      : '-'}
                  </td>
                  <td>{b.benutzer || 'System'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LagerbewegungList;
